/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2020 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import GameSetup, { WagerDisplay } from '../Game/GameSetup';
import RecallManager from '../Game/RecallManager';
import RecoveryManager from '../Game/RecoveryManager';
import Currency, { CurrencyType } from '../Localization/Currency';
import LocalizationComponent from '../Localization/LocalizationComponent';
import ObservableCurrencyComponent from '../Localization/ObservableCurrencyComponent';
import { IPlayState } from '../Server/Object/PlayResult';
import PlayResultComponent from '../Server/PlayResultComponent';
import { IBetConfig, IDenomConfig } from '../Server/SetupResult';
import StateDependencyComponent from '../StateManager/StateDependencyComponent';
import StateEvent from '../StateManager/StateEvent';
import { StateID } from '../StateManager/StateID';
import StateReference from '../StateManager/StateReference';
import InjectableComponent from '../Utility/Component/ComponentReference/InjectableComponent';
import { ReferenceGroup } from '../Utility/Component/ComponentReference/ReferenceGroup';
import ObservableBooleanComponent from '../Utility/Component/Observable/ObservableBooleanComponent';
import ObservableDoubleComponent from '../Utility/Component/Observable/ObservableDoubleComponent';
import ObservableEvent from '../Utility/Component/Observable/ObservableEvent';
import ObservableIntComponent from '../Utility/Component/Observable/ObservableIntComponent';
import CLog, { LogTypes } from '../Utility/ComponentLogger';
import { CurrencyUtil } from '../Utility/CurrencyUtil';
import Decorators from '../Utility/Decorators';
import { isNullOrEmpty } from '../Utility/Misc';
import Bangup from './Bangup';

/** @hidden */
const property: typeof cc._decorator.property = cc._decorator.property;

/**
 * Manages the current wager and its associated bet and denomination configs.
 */
@cc._decorator.ccclass
export default class BetManager extends StateDependencyComponent
{
    /** State on which the bet manager should initialize itself by accessing the setup data. */
    @property({ type: StateReference, visible: true })
    private _initOnState: StateReference = new StateReference(StateID.Initializing, true);

    /** State on which the bet manager should initialize itself by accessing the setup data. */
    @property({ type: StateReference, visible: true })
    protected updateBalanceOnState: StateReference = new StateReference(StateID.PostPlay, true);

    /** State on which the bet manager should initialize itself by accessing the setup data. */
    protected awardStateRefs: StateReference[] =
    [
        new StateReference(StateID.Award, true),
        new StateReference(StateID.Custom, true, ReferenceGroup.MainGame, 'PostAward')
    ];

    /** Observable value for tracking the minimum bet. */
    @property({ type: ObservableCurrencyComponent, visible: true })
    private _minimumBetObservable: ObservableCurrencyComponent = null;

    /** Observable value for tracking the current wager. */
    @property({ type: ObservableCurrencyComponent, visible: true })
    private _betObservable: ObservableCurrencyComponent = null;
    public get BetObservable(): ObservableCurrencyComponent { return this._betObservable; }

    /** Observable value for tracking the current balance. */
    @property({ type: ObservableCurrencyComponent, visible: true })
    private _balanceObservable: ObservableCurrencyComponent = null;
    /** Accessor for get the current observable balance value. */
    public get BalanceMCObservable(): ObservableCurrencyComponent { return this._balanceObservable; }

    /** Observable value for tracking the active currency denomination per credit. */
    @property({ type: ObservableDoubleComponent, visible: true })
    private _activeDenomObservable: ObservableDoubleComponent = null;
    public get ActiveDenomObservable(): ObservableDoubleComponent { return this._activeDenomObservable; }

    /** Observable value for tracking the current bet multiplier. */
    @property({ type: ObservableIntComponent, visible: true })
    protected betMultiplierObservable: ObservableIntComponent = null;

    /** Observable value for tracking the current number of active paylines based on the current wager. */
    @property({ type: ObservableIntComponent, visible: true })
    private _linesObservable: ObservableIntComponent = null;
    public get ActiveLinesObservable(): ObservableIntComponent { return this._linesObservable; }

    /** Observable value for tracking if the user is wagering in cash or credits. */
    @property({ type: ObservableBooleanComponent, visible: true })
    private _isCashObservable: ObservableBooleanComponent = null;
    /** Accessor for the observable is cash or credits value. */
    public get IsCashObservable(): ObservableBooleanComponent { return this._isCashObservable; }

    /** @hidden */
    private _bangupRef: InjectableComponent<Bangup> = new InjectableComponent();
    /** @hidden */
    private _gameSetupRef: InjectableComponent<GameSetup> = new InjectableComponent();
    /** @hidden */
    private _playResultRef: InjectableComponent<PlayResultComponent> = new InjectableComponent();
    /** @hidden */
    private _localRef: InjectableComponent<LocalizationComponent> = new InjectableComponent();
    /** @hidden */
    protected recallManagerRef: InjectableComponent<RecallManager> = new InjectableComponent();
    /** @hidden */
    protected recoveryManagerRef: InjectableComponent<RecoveryManager> = new InjectableComponent();

    protected lastBetCredits: number = 0;

    private _lastWager: IWager = null;
    public get LastWager(): IWager
    {
        if (!this._lastWager) { return this.saveWager(); }
        return this._lastWager;
    }

    private _betScalarConfig: IBetConfig = null;

    /** The collection of [[IBetConfig]]s loaded from the [[GameSetup]]. */
    private _betConfigs: IBetConfig[] = [];
    /** The currently available bet configs for the active denomination. */
    private _activeBetConfigs: IBetConfig[] = [];
    /** The collection of [[IDenomConfig]]s loaded from the [[GameSetup]]. */
    private _denomConfigs: Map<string, IDenomConfig> = null;
    private _needsRecoveryCheck: boolean = false;
    private _hasInit: boolean = false;

    // #region cc.Component Lifecycle

    /** @hidden */
    protected setContexts(): void
    {
        super.setContexts();
        this._gameSetupRef.SetContext(GameSetup);
        this._bangupRef.SetContext(Bangup);
        this._playResultRef.SetContext(PlayResultComponent);
        this._localRef.SetContext(LocalizationComponent);
        this.recallManagerRef.SetContext(RecallManager);
        this.recoveryManagerRef.SetContext(RecoveryManager);
    }

    /** @hidden */
    protected onLoad(): void
    {
        super.onLoad();

        this.betMultiplierObservable.OnChanged(this.betChangedHandler);
        this._linesObservable.OnChanged(this.betChangedHandler);
        this._activeDenomObservable.OnChanged(this.denomChangedHandler);

        this.destructors.push(() => this.betMultiplierObservable.OffChanged(this.betChangedHandler));
        this.destructors.push(() => this._linesObservable.OffChanged(this.betChangedHandler));
        this.destructors.push(() => this._activeDenomObservable.OffChanged(this.denomChangedHandler));
    }

    /** @hidden */
    protected start(): void
    {
        super.start(this._initOnState, this.updateBalanceOnState, this.awardStateRefs);
        this._gameSetupRef.WithValue(gameSetup => gameSetup.OnSetupReceived(this.init));
    }

    /** @hidden */
    protected onStateChangeHandler = (event: StateEvent): void =>
    {
        if (event.State === this.updateBalanceOnState.Value)
        {
            this.checkRecovery();
            this.UpdateBalance();
        }

        if (this.awardStateRefs.some(ref => ref.Value === event.State))
        {
            this._gameSetupRef.WithValue(gameSetup =>
            {
                if (isNullOrEmpty(gameSetup.BetScalers)) { return; }

                this.setBetLevels(this.CurrentBetScaler);
                this._betObservable.OnChangedOnce(obsEvent =>
                {
                    if (!this._needsRecoveryCheck || !obsEvent.RootValueChanged) { return; }
                    // Removes invalid recovered betMultiplier.
                    this.setBetLevels();
                    this._needsRecoveryCheck = false;
                });
            });
        }

        this.completeState(event.State);
    }

    private checkRecovery(): void
    {
        if (!this._needsRecoveryCheck) { return; }
        this._needsRecoveryCheck = false;

        this.setBetLevels();
        this.UnLockBetValues();
    }

    private checkCurrent(): boolean
    {
        return this.recallManagerRef.WithValue(recall => !recall.Recalling, true)
            && this.recoveryManagerRef.WithValue(recovery => !recovery.Recovering, true);
    }

    /** @hidden */
    @Decorators.BindThis()
    protected init = (): void =>
    {
        const gameSetup: GameSetup = this._gameSetupRef.Value;

        this.configureBet();
        this._localRef.Value.Currency = gameSetup.CurrencyCode;

        this._activeDenomObservable.NumberDisplayMod = Currency.ConvertMCToCash;
        this._isCashObservable.Value = gameSetup.WagerDisplay === WagerDisplay.Toggle
            ? true
            : gameSetup.WagerDisplay === WagerDisplay.Cash;
        this._isCashObservable.ForceUpdate();

        // Update initial values.
        this.denomChangedHandler();

        this.completeState(this._initOnState.Value);

        if (this.recoveryManagerRef.WithValue(mgr => mgr.Recovering, false))
        {
            this._needsRecoveryCheck = true;
        }
    }

    private configureBet(): void
    {
        const gameSetup: GameSetup = this._gameSetupRef.Value;
        if (!gameSetup) { return; }

        // Setup denom config data.
        this._denomConfigs = gameSetup.DenomConfigs;
        for (const denom in this._denomConfigs)
        {
            this._activeDenomObservable.AddValidValue(parseInt(denom, 10));
        }

        this._balanceObservable.SetValue(gameSetup.InitialBalanceMC);

        const lastPlayState: IPlayState = gameSetup.LastPlayState;

        let lastBetID: string;
        let betScalar: number;
        if (!lastPlayState || this.checkCurrent())
        {
            this._activeDenomObservable.Value = gameSetup.DefaultBet.denom;
            lastBetID = gameSetup.DefaultBet.betConfigId.toString();
            betScalar = 1;
        }
        else
        {
            this._activeDenomObservable.Value = lastPlayState.denomMc;
            lastBetID = lastPlayState.betConfigId.toString();
            betScalar = lastPlayState.platformBetMult;
        }

        const lastDenomMC: number = this.ActiveDenom;

        // Reset arrays
        this._betConfigs.length = 0;
        this._activeBetConfigs.length = 0;

        // Setup bet config data.
        const activeDenom: IDenomConfig = this._denomConfigs[this.ActiveDenom.toString()];
        for (const betID in gameSetup.BetConfigs)
        {
            const betConfig: IBetConfig = gameSetup.BetConfigs[betID];
            betConfig.betID = parseInt(betID, 10);

            this._betConfigs.push(betConfig);
            if (activeDenom.betConfigIds.findIndex((x) => x === betConfig.betID) >= 0)
            {
                this._activeBetConfigs.push(betConfig);
            }

            if (betID === lastBetID)
            {
                this._activeDenomObservable.Value = lastDenomMC;

                this._linesObservable.Value = betConfig.lines;
                this._linesObservable.AddValidValue(betConfig.lines);

                const multplier: number = betConfig.betMult * betScalar;
                this.betMultiplierObservable.Value = multplier;
                this.betMultiplierObservable.AddValidValue(multplier);
            }
        }
    }

    // #endregion

    // #region Accessors & Mutators

    /** Accessor for the current active denomination of currency per credit. */
    public get ActiveDenom(): number { return this._activeDenomObservable.Value; }
    /** Mutator for the current active denomination for currency per credit. */
    public set ActiveDenom(value: number) { this._activeDenomObservable.Value = value; }

    /** Accessor for the value of the current wager, explicitly in cash. */
    public get CurrentBet(): Currency
    {
        return this._betObservable.Currency;
    }

    public LockBetValues(): void
    {
        this._betObservable.Lock();
        this.betMultiplierObservable.Lock();
        this._activeDenomObservable.Lock();
        this._linesObservable.Lock();
    }

    public UnLockBetValues(): void
    {
        this._betObservable.UnLock();
        this.betMultiplierObservable.UnLock();
        this._activeDenomObservable.UnLock();
        this._linesObservable.UnLock();
    }

    /** Accessor for the active bet configuration. */
    public get CurrentBetConfig(): IBetConfig
    {
        if (!this._activeBetConfigs || this._activeBetConfigs.length === 0) { return null; }
        if (this._betScalarConfig) { return this._betScalarConfig; }

        const betMultiplier: number = this.betMultiplierObservable.Value;
        let closestBetConfig: IBetConfig = null;

        for (const config of this._activeBetConfigs)
        {
            if (config.lines !== this._linesObservable.Value) { continue; }
            if (config.betMult !== betMultiplier) { continue; }

            return config;
        }

        return closestBetConfig || this._activeBetConfigs[0];
    }

    /** Mutator for the active bet configuration. */
    public set CurrentBetConfig(config: IBetConfig)
    {
        this._linesObservable.Value = config.lines;
        this.betMultiplierObservable.Value = config.betMult * this.CurrentBetScaler;
    }

    /** Accessor for the max bet configuration. */
    public get MaxBetConfig(): IBetConfig
    {
        if (this._betScalarConfig) { return this._betScalarConfig; }

        let maxBetInCredits: number = -1;
        let maxBetConfig: IBetConfig = null;
        for (let config of this._activeBetConfigs)
        {
            if (config.maxBet) { return config; }

            maxBetInCredits = Math.max(config.betInCredits, maxBetInCredits);
            if (maxBetInCredits === config.betInCredits)
            {
                maxBetConfig = config;
            }
        }

        return maxBetConfig;
    }

    public SetMaxBet(): void
    {
        this.CurrentBetConfig = this.MaxBetConfig;
    }

    /** Accessor for the value of the current wager, explicitly in credits. */
    public get CurrentBetCredits(): number
    {
        return this._betObservable.Currency.GetValue(CurrencyType.CREDITS);
    }

    /** Mutator for the value of the current wager, explicitly in credits. */
    public set CurrentBetCredits(value: number)
    {
        this._betObservable.Currency.SetValue(value, CurrencyType.CREDITS);
    }

    public get CurrentBetScaler(): number
    {
        return this.CurrentBetCredits / this.CurrentBetConfig.betInCredits;
    }

    /** Accessor for whether or not the player is currently wagering with cash or credits. */
    public get IsCash(): boolean
    {
        this._gameSetupRef.WithValue(gameSetup =>
        {
            if (gameSetup.WagerDisplay === WagerDisplay.Toggle) { return; }
            this._isCashObservable.Value = gameSetup.WagerDisplay === WagerDisplay.Cash;
        });

        return this._isCashObservable.Value;
    }

    /** Accessor for the payout ID of the current [[IDenomConfig]]. */
    public get PayoutID(): number
    {
        return this._denomConfigs[this.ActiveDenom.toString()].payoutIds[0];
    }

    /** Accessor for the paytable of the current [[IDenomConfig]]. */
    public get Paytable(): number
    {
        return this._denomConfigs[this.ActiveDenom.toString()].paytable;
    }

    /** Toggles the wagering system between using credits or currency for display to the user. */
    public ToggleCash(): void
    {
        this._gameSetupRef.WithValue(gameSetup =>
        {
            if (gameSetup.WagerDisplay !== WagerDisplay.Toggle) { return; }

            this._isCashObservable.Value = !this._isCashObservable.Value;
            this.setBetLevels(this.CurrentBetScaler);
        });

    }

    // #endregion

    // #region Observable Handlers

    /**
     * Responds to a change in the current wager.
     * @hidden
     */
    @Decorators.BindThis()
    private betChangedHandler = (event: ObservableEvent<number>): void =>
    {
        if (this.CurrentBetConfig != null)
        {
            this.CurrentBetCredits = this.CurrentBetConfig.betInCredits
                * (this.betMultiplierObservable.Value / this.CurrentBetConfig.betMult);
        }

        for (let config of this._activeBetConfigs)
        {
            if (config.betMult * this.CurrentBetScaler !== this.betMultiplierObservable.Value) { continue; }
            if (config.lines !== this._linesObservable.Value) { continue; }

            this.lastBetCredits = config.betInCredits;
            this._betObservable.Currency.SetValue(config.betInCredits * this.CurrentBetScaler, CurrencyType.CREDITS);
            return;
        }

        this.lastBetCredits = this._betObservable.Currency.GetValue(CurrencyType.CREDITS);
    }

    /**
     * Responds to a change in the current denomination.
     * @hidden
     */
    @Decorators.BindThis()
    private denomChangedHandler = (): void =>
    {
        if (!this._activeBetConfigs || this._activeBetConfigs.length === 0) { return; }

        this._activeBetConfigs = [];

        let activeDenom: IDenomConfig = this._denomConfigs[this._activeDenomObservable.Value.toString()];
        for (let betConfig of this._betConfigs)
        {
            if (activeDenom.betConfigIds.findIndex((x) => x === betConfig.betID) > -1)
            {
                this._activeBetConfigs.push(betConfig);
            }
        }

        this.setBetLevels();
    }

    // #endregion

    // #region Wagering

    /**
     * Increases value displayed as the player's current available balance.
     * @param balanceDelta How much to change the balance by.
     */
    public AddBalance(balanceDelta: Currency): void
    {
        this._balanceObservable.Currency.Add(balanceDelta);
    }

    /**
     * Update the balance from the [[IPlayState]].
     */
    public UpdateBalance = (): number =>
    {
        const playResult: PlayResultComponent = this._playResultRef.Value;
        if (!playResult || !playResult.PlayState || playResult.IsBonusSpinActive()) { return; }

        const currentBalanceMc: number = this._balanceObservable.Currency.GetValue();
        const newBalanceMc: number = playResult.PlayState.balanceMc;

        if (currentBalanceMc !== newBalanceMc)
        {
            CLog.warn(`Local / Server Balance Mismatch. Local: [${currentBalanceMc}] Server: [${newBalanceMc}]`,
                LogTypes.Communicator, this);
        }

        this._balanceObservable.SetValue(newBalanceMc);
        return newBalanceMc;
    }

    /**
     * Set balance directly.
     * @param amountMc Amount in millicent to set balance to.
     */
    public SetBalance = (amountMc: number) =>
    {
        this._balanceObservable.SetValue(amountMc);
    }

    /**
     * Get balance.
     */
    public GetBalance(): Currency
    {
        return this._balanceObservable.Currency;
    }

    /**
     * Deducts the current wager from the player's balance display.
     * @return Success.
     */
    public PlaceBet(): boolean
    {
        if (this._balanceObservable.Currency
                .Difference(this._betObservable.Currency)
                .GetValue() < 0)
        {
            return false;
        }

        this._balanceObservable.Currency.Subtract(this._betObservable.Currency);
        this.saveWager();
        return true;
    }

    private saveWager(): IWager
    {
        return this._lastWager =
        {
            Balance: this._balanceObservable.Currency.Clone(false),
            BetCredits: this._betObservable.Currency.GetValue(CurrencyType.CREDITS),
            BetScaler: this.CurrentBetScaler,
            DenomMc: this._activeDenomObservable.Value
        };
    }

    /**
     * Get currency display string based on BetManager settings.
     * @param currency Currency to be converted to string representation.
     * @returns String representation of currency.
     */
    public GetCurrencyDisplay(currency: Currency): string
    {
        if (this.IsCash)
        {
            let currencyAmt: number = currency.GetValue(CurrencyType.CASH);
            let currencyString: string = CurrencyUtil.GetCurrencyString(this._localRef.Value, currencyAmt);
            return currencyString;
        }

        return currency
            .GetValue(CurrencyType.CREDITS)
            .toLocaleString(this._localRef.Value.CultureKey, { useGrouping: true });
    }

    /**
     * Helper functon for determining the active lines and available bet configs based on the active denom.
     * @hidden
     */
    private setBetLevels(overrideBetMult: number = -1): void
    {
        if (!this._activeBetConfigs || this._activeBetConfigs.length === 0) { return; }

        this._betObservable.ClearValidValues();
        this.betMultiplierObservable.ClearValidValues();
        this._linesObservable.ClearValidValues();

        const gameSetup: GameSetup = this._gameSetupRef.Value;
        let minBetCredits: number = 0;
        const setMinBetCredits: (arg: number) => void = credits =>
        {
            minBetCredits = Math.min(credits, minBetCredits);
        };

        if (!isNullOrEmpty(gameSetup.BetScalers))
        {
            const currentBetScalar: number = this.checkCurrent()
                ? gameSetup.DefaultBetScale
                : this.CurrentBetScaler;
            const betConfig: IBetConfig = this._betScalarConfig = this._activeBetConfigs[0];

            gameSetup.BetScalers.forEach(betScaler =>
            {
                setMinBetCredits(betConfig.betInCredits);

                this._betObservable.AddValidValue(betConfig.betInCredits * betScaler, CurrencyType.CREDITS);
                this.betMultiplierObservable.AddValidValue(betConfig.betMult * betScaler);
                this._linesObservable.AddValidValue(betConfig.lines);
            }, this);

            if (overrideBetMult > -1)
            {
                this.betMultiplierObservable.AddValidValue(betConfig.betMult * overrideBetMult);
            }
            else if (this.checkCurrent() && !this._hasInit)
            {
                this._hasInit = true;
                this.betMultiplierObservable.SetValue(betConfig.betMult * currentBetScalar);
            }
        }
        else
        {
            this._activeBetConfigs.forEach(betConfig =>
            {
                setMinBetCredits(betConfig.betInCredits);

                this._betObservable.AddValidValue(betConfig.betInCredits, CurrencyType.CREDITS);
                this.betMultiplierObservable.AddValidValue(betConfig.betMult);
                this._linesObservable.AddValidValue(betConfig.lines);
            }, this);
        }

        this._betObservable.Currency
            .SetValue(this.CurrentBetConfig.betInCredits * this.CurrentBetScaler, CurrencyType.CREDITS);

        if (this._minimumBetObservable)
        {
            this._minimumBetObservable.Value = minBetCredits;
        }
    }

    // #endregion
}

interface IWager
{
    Balance: Currency;
    BetCredits: number;
    BetScaler: number;
    DenomMc: number;
}
